import { useCallback, useEffect, useRef, useState } from "react"
import type { MarkdownActiveMetaDataWord } from "@/shared/api/generated"
import { activePlayer } from "@/entities/audio-engine"

interface UseMarkdownWordTrackerResult {
	activeWord: MarkdownActiveMetaDataWord | null
	autoScroll: boolean
	setAutoScroll: (v: boolean) => void
}

/**
 * How long a word keeps the highlight past its own end_ts when it's the last
 * loaded word (end of window / not-yet-aligned region ahead). Bounded so the
 * miss path can kick in and slide/re-fetch the window.
 */
const TRAILING_HOLD_S = 1

/**
 * Continuous "karaoke" lookup: the active word is the last one whose
 * start_ts <= time, held until the NEXT word starts — not just within its
 * own [start_ts, end_ts]. Honest gaps between words and unaligned runs
 * (words that failed alignment have no timings at all) therefore don't
 * blink the highlight off; the previous word simply holds a bit longer.
 */
const findActiveWord = (
	words: MarkdownActiveMetaDataWord[],
	time: number
): MarkdownActiveMetaDataWord | null => {
	if (!words || words.length === 0) return null
	if (time < words[0].start_ts) return null

	// Last word with start_ts <= time
	let left = 0
	let right = words.length - 1
	let found = -1
	while (left <= right) {
		const mid = Math.floor((left + right) / 2)
		if (words[mid].start_ts <= time) {
			found = mid
			left = mid + 1
		} else {
			right = mid - 1
		}
	}
	if (found === -1) return null

	const word = words[found]
	const isLastLoaded = found === words.length - 1

	// Past the end of the loaded window: release after a short hold so the
	// miss logic can locate the page / re-fetch the window.
	if (isLastLoaded && time > word.end_ts + TRAILING_HOLD_S) return null

	return word
}

const SEEK_THRESHOLD = 2

/**
 * Karaoke-style highlight for the markdown viewer.
 *
 * Unlike the PDF tracker (absolute-positioned overlays keyed by page/word
 * index), markdown words live directly in the rendered HTML as
 * <span id="wN" class="tts-word"> — the tracker resolves them by id and
 * toggles the `active` class imperatively (React never re-renders the
 * dangerouslySetInnerHTML content, so the class survives).
 */
export const useMarkdownWordTracker = (
	words: MarkdownActiveMetaDataWord[],
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
	onActivePageChange?: (pageIndex: number) => void,
	onMiss?: (position: number) => void,
	/** Scroll to a page whose DOM isn't mounted (virtualized out) */
	onScrollToPage?: (pageIndex: number) => void
): UseMarkdownWordTrackerResult => {
	const [activeWord, setActiveWord] = useState<MarkdownActiveMetaDataWord | null>(
		null
	)
	const [autoScroll, setAutoScroll] = useState(true)
	const wordsRef = useRef(words)
	const activeWordRef = useRef<MarkdownActiveMetaDataWord | null>(null)
	const activeElRef = useRef<HTMLElement | null>(null)
	const autoScrollRef = useRef(true)
	const lastPositionRef = useRef(0)
	/** performance.now() of the first consecutive miss, null when not missing */
	const missStartRef = useRef<number | null>(null)
	/** performance.now() of the last throttled visibility/remount check */
	const lastEnsureRef = useRef(0)
	const wasPlayingRef = useRef(false)
	const activePageRef = useRef(-1)

	wordsRef.current = words
	autoScrollRef.current = autoScroll

	// ─── Imperative highlight + scroll ───────────────────────

	const clearHighlight = useCallback(() => {
		activeElRef.current?.classList.remove("active")
		activeElRef.current = null
	}, [])

	const applyHighlight = useCallback(
		(word: MarkdownActiveMetaDataWord, instant: boolean) => {
			const element =
				word.wordIdx !== null && word.wordIdx !== undefined
					? document.getElementById(`w${word.wordIdx}`)
					: null

			if (element !== activeElRef.current) {
				clearHighlight()
				if (element) {
					element.classList.add("active")
					activeElRef.current = element
				}
			}

			// Word's page isn't in the DOM (virtualized out, or its HTML is
			// still loading) — scroll to the page slot instead; once it
			// mounts, the next ticks resolve the span and fine-scroll to it.
			if (!element) {
				if (autoScrollRef.current) onScrollToPage?.(word.pageIndex)
				return
			}
			if (!autoScrollRef.current || !scrollContainerRef.current) return

			const container = scrollContainerRef.current
			const viewportHeight = container.clientHeight
			const containerRect = container.getBoundingClientRect()
			const wordRect = element.getBoundingClientRect()
			const wordTopInViewport = wordRect.top - containerRect.top
			const wordBottomInViewport = wordRect.bottom - containerRect.top

			const offScreen =
				wordBottomInViewport < 0 || wordTopInViewport > viewportHeight
			const nearEdge =
				wordTopInViewport < viewportHeight * 0.2 ||
				wordBottomInViewport > viewportHeight * 0.8

			if (instant || offScreen || nearEdge) {
				const wordTopAbsolute =
					wordRect.top - containerRect.top + container.scrollTop
				container.scrollTo({
					top: wordTopAbsolute - viewportHeight * 0.3,
					behavior: instant || offScreen ? "instant" : "smooth",
				})
			}
		},
		[clearHighlight, scrollContainerRef, onScrollToPage]
	)

	// Re-enabling auto-scroll must immediately bring the user back to the
	// active word (or its page slot if that page is virtualized out) — the
	// poll loop alone only scrolls on word changes.
	const setAutoScrollAndSnap = useCallback(
		(value: boolean) => {
			setAutoScroll(value)
			autoScrollRef.current = value
			if (value && activeWordRef.current) {
				applyHighlight(activeWordRef.current, true)
			}
		},
		[applyHighlight]
	)

	// ─── Main poll loop ──────────────────────────────────────
	// Runs on requestAnimationFrame: both audio engines update
	// currentPosition per frame, so the highlight toggles the very frame a
	// word's start_ts is crossed (a fixed interval added up to that much
	// latency on every word). Per-frame work is a binary search + ref
	// comparisons; DOM reads/writes only happen on change or throttled.

	useEffect(() => {
		let rafId: number | null = null

		const check = () => {
			rafId = requestAnimationFrame(check)

			const currentPos = activePlayer.currentPosition
			const isPlaying = activePlayer.isPlaying
			const justResumed = !wasPlayingRef.current && isPlaying
			wasPlayingRef.current = isPlaying

			// Detect seek
			const jumped =
				Math.abs(currentPos - lastPositionRef.current) > SEEK_THRESHOLD
			lastPositionRef.current = currentPos

			if (jumped) {
				missStartRef.current = null
				activeWordRef.current = null
				setActiveWord(null)
				clearHighlight()
			}

			if (currentPos <= 0) return

			const found = findActiveWord(wordsRef.current, currentPos)

			if (found) {
				missStartRef.current = null

				if (found.pageIndex !== activePageRef.current) {
					activePageRef.current = found.pageIndex
					onActivePageChange?.(found.pageIndex)
				}

				const changed =
					!activeWordRef.current ||
					activeWordRef.current.wordIdx !== found.wordIdx

				activeWordRef.current = found
				if (changed) setActiveWord(found)

				const now = performance.now()
				if (changed || justResumed || jumped) {
					lastEnsureRef.current = now
					applyHighlight(found, justResumed || jumped)
				} else if (now - lastEnsureRef.current >= 250) {
					// Throttled check (layout reads are not free at 60fps):
					// ensure the word is visible (user scrolled away while
					// playing) and that the highlight survived a virtualization
					// remount of its page (dangerouslySetInnerHTML recreates
					// the span without the imperative `active` class).
					lastEnsureRef.current = now
					if (
						(autoScrollRef.current && isPlaying) ||
						!activeElRef.current?.isConnected
					) {
						applyHighlight(found, false)
					}
				}
			} else if (isPlaying) {
				// Word for this position isn't in the loaded window (big seek
				// into an unloaded region, or processing content not aligned
				// yet) — after 750ms of continuous misses let the owner locate
				// the page / re-fetch the window. Time-based, not tick-based:
				// frames are ~16ms and honest gaps between words must not
				// trigger a re-scan.
				const now = performance.now()
				if (missStartRef.current === null) {
					missStartRef.current = now
				} else if (now - missStartRef.current >= 750 && onMiss) {
					missStartRef.current = null
					onMiss(currentPos)
				}
			}
		}

		rafId = requestAnimationFrame(check)

		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId)
		}
	}, [applyHighlight, clearHighlight, onActivePageChange, onMiss])

	// Clean up the imperative class on unmount
	useEffect(() => clearHighlight, [clearHighlight])

	return { activeWord, autoScroll, setAutoScroll: setAutoScrollAndSnap }
}
